import React, { useEffect, useState } from "react";
import { api, downloadAuthedFile } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash2, Save, FileText, Loader2, Download } from "lucide-react";
import { toast } from "sonner";

const fmt = (n) => `${Number(n || 0).toLocaleString("fr-FR")} GNF`;

export default function SaisiesPage() {
  const [entries, setEntries] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [journals, setJournals] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [filterJournal, setFilterJournal] = useState("ALL");
  const today = new Date().toISOString().slice(0, 10);

  const [form, setForm] = useState({
    date: today,
    journal: "CA",
    reference: "",
    description: "",
    lines: [
      { account_code: "", label: "", debit: 0, credit: 0 },
      { account_code: "", label: "", debit: 0, credit: 0 },
    ],
  });

  const load = async () => {
    try {
      const params = filterJournal !== "ALL" ? { journal: filterJournal } : {};
      const [{ data: es }, { data: as }, { data: js }] = await Promise.all([
        api.get("/accounting/entries", { params }),
        api.get("/accounting/accounts"),
        api.get("/accounting/journals"),
      ]);
      setEntries(es);
      setAccounts(as);
      setJournals(js);
    } catch (e) {
      toast.error("Erreur de chargement");
    }
  };
  useEffect(() => { load(); }, [filterJournal]);

  const addLine = () => setForm({ ...form, lines: [...form.lines, { account_code: "", label: "", debit: 0, credit: 0 }] });
  const removeLine = (i) => setForm({ ...form, lines: form.lines.filter((_, idx) => idx !== i) });
  const updateLine = (i, field, val) => {
    const lines = [...form.lines];
    lines[i] = { ...lines[i], [field]: val };
    setForm({ ...form, lines });
  };

  const totalDebit = form.lines.reduce((s, l) => s + Number(l.debit || 0), 0);
  const totalCredit = form.lines.reduce((s, l) => s + Number(l.credit || 0), 0);
  const isBalanced = Math.abs(totalDebit - totalCredit) < 0.01 && totalDebit > 0;

  const save = async () => {
    if (!isBalanced) return toast.error("Écriture non équilibrée (Débit = Crédit requis)");
    if (!form.description) return toast.error("Description obligatoire");
    setSaving(true);
    try {
      const payload = {
        ...form,
        lines: form.lines.map(l => ({ ...l, debit: Number(l.debit) || 0, credit: Number(l.credit) || 0 })),
      };
      const { data } = await api.post("/accounting/entries", payload);
      toast.success(`Écriture ${data.piece_no} enregistrée`);
      setOpen(false);
      setForm({
        date: today, journal: form.journal, reference: "", description: "",
        lines: [{ account_code: "", label: "", debit: 0, credit: 0 }, { account_code: "", label: "", debit: 0, credit: 0 }],
      });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur d'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const exportCSV = () => {
    const params = new URLSearchParams();
    if (filterJournal !== "ALL") params.append("journal", filterJournal);
    downloadAuthedFile(`/accounting/export/entries.csv?${params.toString()}`, "ecritures.csv")
      .catch(() => toast.error("Export impossible"));
  };

  const removeEntry = async (id) => {
    if (!confirm("Supprimer cette écriture ? (action réservée à la direction)")) return;
    try {
      await api.delete(`/accounting/entries/${id}`);
      toast.success("Écriture supprimée");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Suppression refusée");
    }
  };

  return (
    <div className="space-y-6" data-testid="saisies-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="chip mb-2"><FileText className="h-3 w-3" /><span>SAISIES COMPTABLES</span></div>
          <h1 className="font-heading font-extrabold text-3xl text-[#1E3A8A]">Écritures comptables</h1>
          <p className="text-slate-500 mt-1">Enregistrez toutes les opérations financières en partie double.</p>
        </div>
        <div className="flex gap-2 items-center">
          <Select value={filterJournal} onValueChange={setFilterJournal}>
            <SelectTrigger className="w-48" data-testid="filter-journal"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">Tous les journaux</SelectItem>
              {journals.map(j => <SelectItem key={j.code} value={j.code}>{j.code} — {j.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={exportCSV} data-testid="export-entries"><Download className="h-4 w-4 mr-2" />Excel</Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#166534] hover:bg-[#14532d] text-white" data-testid="open-new-entry"><Plus className="h-4 w-4 mr-2" />Nouvelle écriture</Button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl">
              <DialogHeader><DialogTitle>Nouvelle écriture comptable</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                  <div>
                    <Label>Date *</Label>
                    <Input type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})} data-testid="entry-date" />
                  </div>
                  <div>
                    <Label>Journal *</Label>
                    <Select value={form.journal} onValueChange={(v) => setForm({...form, journal: v})}>
                      <SelectTrigger data-testid="entry-journal"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {journals.map(j => <SelectItem key={j.code} value={j.code}>{j.code} — {j.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="md:col-span-2">
                    <Label>Référence (pièce justificative)</Label>
                    <Input value={form.reference} onChange={e => setForm({...form, reference: e.target.value})} placeholder="FAC-2026-001" data-testid="entry-ref" />
                  </div>
                </div>
                <div>
                  <Label>Description *</Label>
                  <Textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} placeholder="Encaissement scolarité — Mariama Diallo CE2 — Janvier 2026" rows={2} data-testid="entry-desc" />
                </div>

                <div className="border border-slate-200 rounded-lg overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50">
                      <tr className="text-left">
                        <th className="px-3 py-2 font-semibold">Compte</th>
                        <th className="px-3 py-2 font-semibold">Libellé</th>
                        <th className="px-3 py-2 font-semibold text-right">Débit</th>
                        <th className="px-3 py-2 font-semibold text-right">Crédit</th>
                        <th className="px-3 py-2"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {form.lines.map((l, i) => (
                        <tr key={i} className="border-t border-slate-100">
                          <td className="px-3 py-2 w-72">
                            <Select value={l.account_code} onValueChange={(v) => updateLine(i, "account_code", v)}>
                              <SelectTrigger data-testid={`line-account-${i}`}><SelectValue placeholder="Choisir un compte..." /></SelectTrigger>
                              <SelectContent className="max-h-72">
                                {accounts.map(a => <SelectItem key={a.code} value={a.code}>{a.code} — {a.name}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="px-3 py-2"><Input value={l.label} onChange={e => updateLine(i, "label", e.target.value)} placeholder="Détail" data-testid={`line-label-${i}`} /></td>
                          <td className="px-3 py-2 w-36"><Input type="number" min="0" value={l.debit} onChange={e => updateLine(i, "debit", e.target.value)} className="text-right tabular-nums" data-testid={`line-debit-${i}`} /></td>
                          <td className="px-3 py-2 w-36"><Input type="number" min="0" value={l.credit} onChange={e => updateLine(i, "credit", e.target.value)} className="text-right tabular-nums" data-testid={`line-credit-${i}`} /></td>
                          <td className="px-3 py-2"><Button variant="ghost" size="sm" onClick={() => removeLine(i)} disabled={form.lines.length <= 2}><Trash2 className="h-4 w-4 text-red-500" /></Button></td>
                        </tr>
                      ))}
                      <tr className="bg-slate-50 font-bold">
                        <td colSpan={2} className="px-3 py-2 text-right">TOTAUX</td>
                        <td className="px-3 py-2 text-right tabular-nums">{fmt(totalDebit)}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{fmt(totalCredit)}</td>
                        <td></td>
                      </tr>
                      <tr className={isBalanced ? "bg-green-50" : "bg-red-50"}>
                        <td colSpan={5} className="px-3 py-2 text-center text-sm font-semibold">
                          {isBalanced ? `✓ Écriture équilibrée (${fmt(totalDebit)})` : `✗ Différence : ${fmt(Math.abs(totalDebit - totalCredit))} — l'écriture doit être équilibrée`}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div className="flex justify-between items-center">
                  <Button variant="outline" onClick={addLine} data-testid="add-line"><Plus className="h-4 w-4 mr-2" />Ajouter une ligne</Button>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
                <Button onClick={save} disabled={!isBalanced || saving} className="bg-[#166534] hover:bg-[#14532d] text-white" data-testid="save-entry">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                  Enregistrer
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* List */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3">N° Pièce</th>
                <th className="px-4 py-3">Journal</th>
                <th className="px-4 py-3">Description</th>
                <th className="px-4 py-3">Référence</th>
                <th className="px-4 py-3 text-right">Total</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {entries.map(e => (
                <tr key={e.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`entry-${e.piece_no}`}>
                  <td className="px-4 py-3 font-medium">{e.date}</td>
                  <td className="px-4 py-3 font-mono text-xs">{e.piece_no}</td>
                  <td className="px-4 py-3"><span className="inline-block px-2 py-0.5 rounded bg-blue-100 text-[#1E3A8A] font-bold text-xs">{e.journal}</span></td>
                  <td className="px-4 py-3">{e.description}</td>
                  <td className="px-4 py-3 text-slate-500 text-xs">{e.reference}</td>
                  <td className="px-4 py-3 text-right tabular-nums font-semibold">{fmt(e.total_debit)}</td>
                  <td className="px-4 py-3 text-right"><Button variant="ghost" size="sm" onClick={() => removeEntry(e.id)} data-testid={`delete-${e.piece_no}`}><Trash2 className="h-4 w-4 text-red-500" /></Button></td>
                </tr>
              ))}
              {entries.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-12 text-center text-slate-400">Aucune écriture pour ce filtre. Cliquez sur &laquo;&nbsp;Nouvelle écriture&nbsp;&raquo; pour commencer.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
